import React, { useCallback, useEffect, useMemo } from 'react';
import { useAPIWrapper, DetailBySchema, DetailFieldLayout } from 'drf-react-by-schema';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';

import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import PauseIcon from '@mui/icons-material/Pause';

import { Layout } from 'src/styles';

import { useAppSelector } from 'src/app/hooks';
import { setPage } from 'src/features/pageSlice';
import { appDispatch } from 'src/utils/utils';
import {
  GENERAL_ADMINS,
  getMesocarpoProcessingLabel,
  MesocarpoProcessingStatus,
} from 'src/constants';

import AsmubipButton from 'src/components/AsmubipButton';
import { PageType } from 'src/constants/PageType';
import { detailAccordion, detailNonAccordion } from 'src/styles/layout';
import { useHasPermissionsMulti } from 'src/hooks/useHasPermissions';

const MODEL = 'mesocarpo_processing';

function MesocarpoProcessingDetail() {
  const { id, formValues, perfil, isMobile } = useAppSelector((state) => state.page);
  const { pageForm, loadSinglePageData } = useAPIWrapper();
  const navigate = useNavigate();
  const [isAdmin] = useHasPermissionsMulti([GENERAL_ADMINS]);
  const [URLSearchParams] = useSearchParams();
  const savedType = URLSearchParams.get('type');

  const key = MODEL;

  const handleEditClick = useCallback(() => {
    navigate(`/${key}/edit/${id}`);
  }, [id]);

  const status = useMemo(() => {
    return formValues?.status?.value as MesocarpoProcessingStatus | undefined;
  }, [formValues?.status]);

  const perfilEntreposto = useMemo(() => perfil?.entreposto, [perfil?.entreposto]);

  const statusName = useMemo(() => {
    if (!status) {
      return;
    }
    return getMesocarpoProcessingLabel({ status });
  }, [status]);

  const fieldsLayout = useMemo(() => {
    const isComplete = status === MesocarpoProcessingStatus.COMPLETE;
    const newFieldsLayout: DetailFieldLayout[] = [
      {
        title: 'Lote e Produtos',
        rows: [
          'pctes_um_quarto',
          'pctes_metade',
          'data_empacotamento',
          'peso_perda_empacotamento',
          'comentarios_empacotamento',
        ],
        ...detailNonAccordion,
      },
      {
        title: 'Armazenamento',
        rows: ['peso_armazenado', 'observacoes', 'comentarios'],
        isAccordion: isComplete,
        isAccordionOpen: true,
        ...(isComplete ? detailAccordion : detailNonAccordion),
      },
      {
        title: 'Processamento',
        rows: ['data_processamento', 'peso_processado', 'peso_perda', 'peso_residuo'],
        isAccordion: isComplete,
        isAccordionOpen: true,
        ...(isComplete ? detailAccordion : detailNonAccordion),
      },
      {
        title: 'Lotes de produção',
        rows: [
          ...(perfilEntreposto ? [] : ['entreposto']),
          'data_novo_peso_flocos_seco',
          'lotes_producao',
          'novo_peso_flocos_seco',
        ],
        isAccordion: isComplete,
        isAccordionOpen: true,
        ...(isComplete ? detailAccordion : detailNonAccordion),
      },
    ];

    return newFieldsLayout;
  }, [perfilEntreposto, status]);

  useEffect(() => {
    if (!perfil || !id || !key) {
      return;
    }

    if (!isAdmin && (!perfilEntreposto || `${perfilEntreposto.id}` !== id)) {
      navigate(`/${key}`);
    }

    const loadAllDataAsync = async () => {
      const values = await loadSinglePageData({
        model: MODEL,
        objId: id,
      });
      if (values) {
        const pageTitle = !savedType
          ? 'Beneficiamento do mesocarpo'
          : savedType === PageType.CREATE
            ? 'Produção concluída!'
            : 'Produção editada!';
        appDispatch(
          setPage({
            id,
            model: MODEL,
            formValues: values,
            title: pageTitle,
          })
        );
      }
    };
    loadAllDataAsync();
  }, [id, key, savedType, perfil, isAdmin, perfilEntreposto]);

  if (
    !perfil ||
    !pageForm?.schema ||
    !pageForm.columns ||
    !formValues ||
    !id ||
    !fieldsLayout ||
    !status ||
    !statusName
  ) {
    return null;
  }

  return (
    <>
      <Card>
        <CardContent sx={Layout.productionDetailCardContent}>
          <Box sx={Layout.productionDetailCardIconWrapper}>
            {status === MesocarpoProcessingStatus.COMPLETE ? (
              <CheckIcon sx={Layout.productionCardIcon} />
            ) : (
              <PauseIcon sx={Layout.productionCardIcon} />
            )}
          </Box>
          <Typography variant="h6">Beneficiamento</Typography>
          <Typography variant="body1">Lote {formValues.lote}</Typography>
        </CardContent>
      </Card>
      <DetailBySchema
        schema={pageForm.schema}
        columns={pageForm.columns}
        values={formValues}
        fieldsLayout={fieldsLayout}
        elevation={0}
      />
      <Box
        sx={
          isMobile
            ? { paddingBottom: 12 }
            : {
                display: 'flex',
                justifyContent: 'flex-end',
                paddingBottom: 4,
                marginTop: 2,
                gap: 2,
              }
        }>
        <AsmubipButton
          variant="contained-secondary"
          aria-label="editar"
          size="large"
          fullWidth={isMobile}
          onClick={handleEditClick}
          startIcon={<EditIcon />}>
          Editar
        </AsmubipButton>
      </Box>
    </>
  );
}

export default React.memo(MesocarpoProcessingDetail);
